import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ScrollArea } from "./ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SignaturePad } from "./SignaturePad";
import { Checkbox } from "./ui/checkbox";
import { AlertTriangle, FileCheck, Loader2, Languages } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { languages as allLanguages } from "../lib/languages";

const LANGUAGES = allLanguages.map(l => ({ code: l.code, label: l.name }));

const CONSENT_TRANSLATIONS = {
    en: [
        {
            title: "1. Purpose of the Recording",
            content: [
                "You are participating in an intake / assessment session with the above-named organization.",
                "The purpose of this session is to:",
                "• Understand your current situation",
                "• Conduct a structured needs assessment",
                "• Generate a case report to support service planning",
                "• Assist the case worker in identifying appropriate supports"
            ]
        },
        // ... (I will keep the existing English structure but ensure it matches correct JSON format for others)
        {
            title: "2. Audio Recording",
            content: [
                "With your permission:",
                "• This session will be audio recorded.",
                "• The recording will be used solely for assessment and documentation purposes.",
                "• The recording will not be used for marketing, research, or public distribution.",
                "The recording will be processed through secure software to convert speech into text (transcription)."
            ]
        },
        {
            title: "3. Use of Artificial Intelligence (AI)",
            content: [
                "To assist with documentation and assessment:",
                "• The audio recording will be converted into a written transcript.",
                "• The transcript will be analyzed using secure artificial intelligence (AI) systems.",
                "• The AI system helps generate:",
                "  • Assessment scoring",
                "  • Case summaries",
                "  • Service planning notes",
                "  • Structured reports",
                "Important:",
                "• AI is used as a support tool only.",
                "• A human case worker reviews and may edit all outputs.",
                "• Final decisions are made by the case worker, not by AI."
            ]
        },
        {
            title: "4. Data Handling and Privacy",
            content: [
                "Your information will be:",
                "• Stored on secure servers.",
                "• Protected using encryption during transmission.",
                "• Accessible only to authorized personnel involved in your care.",
                "Your information will NOT:",
                "• Be sold",
                "• Be shared with third parties without your consent (unless required by law)",
                "• Be used for AI model training (where opt-out settings are applied)"
            ]
        },
        {
            title: "5. Data Retention",
            content: [
                "• The audio recording and related documentation may be retained for organizational and legal record-keeping requirements.",
                "• Records may be deleted in accordance with the organization’s data retention policy.",
                "• You may request information about how long your data is retained."
            ]
        },
        {
            title: "6. Your Rights",
            content: [
                "You have the right to:",
                "• Ask questions about how your data is used.",
                "• Withdraw consent before or during the recording.",
                "• Request access to your records (subject to organizational policy and legal limits).",
                "• Request correction of inaccurate information.",
                "If you withdraw consent:",
                "• Recording will stop immediately.",
                "• Previously collected data may be retained if required for legal or documentation purposes."
            ]
        },
        {
            title: "7. Voluntary Participation",
            content: [
                "Your participation in this recording is voluntary.",
                "You may decline to be recorded.",
                "Declining will not automatically prevent you from receiving services, though documentation methods may differ."
            ]
        },
        {
            title: "8. Acknowledgment and Consent",
            content: [
                "By signing below, you confirm that:",
                "• You understand that this session will be audio recorded.",
                "• You understand that AI tools will be used to transcribe and assist with assessment.",
                "• You have had the opportunity to ask questions.",
                "• You voluntarily consent to proceed."
            ]
        }
    ],
    fr: [
        {
            title: "1. But de l'enregistrement",
            content: [
                "Vous participez à une session d'admission / évaluation avec l'organisation nommée ci-dessus.",
                "Le but de cette session est de :",
                "• Comprendre votre situation actuelle",
                "• Mener une évaluation structurée des besoins",
                "• Générer un rapport de cas pour soutenir la planification des services",
                "• Aider le travailleur social à identifier les soutiens appropriés"
            ]
        },
        {
            title: "2. Enregistrement audio",
            content: [
                "Avec votre permission :",
                "• Cette session sera enregistrée (audio).",
                "• L'enregistrement sera utilisé uniquement à des fins d'évaluation et de documentation.",
                "• L'enregistrement ne sera pas utilisé pour le marketing, la recherche ou la distribution publique.",
                "L'enregistrement sera traité par un logiciel sécurisé pour convertir la parole en texte (transcription)."
            ]
        },
        {
            title: "3. Utilisation de l'intelligence artificielle (IA)",
            content: [
                "Pour aider à la documentation et à l'évaluation :",
                "• L'enregistrement audio sera converti en une transcription écrite.",
                "• La transcription sera analysée à l'aide de systèmes d'intelligence artificielle (IA) sécurisés.",
                "• Le système d'IA aide à générer :",
                "  • La notation de l'évaluation",
                "  • Les résumés de cas",
                "  • Les notes de planification de service",
                "  • Des rapports structurés",
                "Important :",
                "• L'IA est utilisée uniquement comme outil de support.",
                "• Un travailleur social humain examine et peut modifier toutes les sorties.",
                "• Les décisions finales sont prises par le travailleur social, et non par l'IA."
            ]
        },
        {
            title: "4. Traitement des données et confidentialité",
            content: [
                "Vos informations seront :",
                "• Stockées sur des serveurs sécurisés.",
                "• Protégées par cryptage lors de la transmission.",
                "• Accessibles uniquement au personnel autorisé impliqué dans vos soins.",
                "Vos informations NE SERONT PAS :",
                "• Vendues",
                "• Partagées avec des tiers sans votre consentement (sauf si la loi l'exige)",
                "• Utilisées pour la formation de modèles d'IA (lorsque les paramètres d'exclusion sont appliqués)"
            ]
        },
        {
            title: "5. Conservation des données",
            content: [
                "• L'enregistrement audio et la documentation connexe peuvent être conservés pour des exigences organisationnelles et légales.",
                "• Les dossiers peuvent être supprimés conformément à la politique de conservation des données de l'organisation.",
                "• Vous pouvez demander des informations sur la durée de conservation de vos données."
            ]
        },
        {
            title: "6. Vos droits",
            content: [
                "Vous avez le droit de :",
                "• Poser des questions sur l'utilisation de vos données.",
                "• Retirer votre consentement avant ou pendant l'enregistrement.",
                "• Demander l'accès à vos dossiers (sous réserve de la politique organisationnelle et des limites légales).",
                "• Demander la correction d'informations inexactes.",
                "Si vous retirez votre consentement :",
                "• L'enregistrement s'arrêtera immédiatement.",
                "• Les données collectées précédemment peuvent être conservées si nécessaire à des fins légales ou documentaires."
            ]
        },
        {
            title: "7. Participation volontaire",
            content: [
                "Votre participation à cet enregistrement est volontaire.",
                "Vous pouvez refuser d'être enregistré.",
                "Le refus ne vous empêchera pas automatiquement de recevoir des services, bien que les méthodes de documentation puissent différer."
            ]
        },
        {
            title: "8. Reconnaissance et consentement",
            content: [
                "En signant ci-dessous, vous confirmez que :",
                "• Vous comprenez que cette session sera enregistrée (audio).",
                "• Vous comprenez que des outils d'IA seront utilisés pour transcrire et aider à l'évaluation.",
                "• Vous avez eu l'occasion de poser des questions.",
                "• Vous consentez volontairement à procéder."
            ]
        }
    ],
    es: [
        {
            title: "1. Propósito de la Grabación",
            content: [
                "Usted está participando en una sesión de admisión / evaluación con la organización mencionada anteriormente.",
                "El propósito de esta sesión es:",
                "• Comprender su situación actual",
                "• Realizar una evaluación estructurada de necesidades",
                "• Generar un informe de caso para apoyar la planificación de servicios",
                "• Ayudar al trabajador social a identificar los apoyos adecuados"
            ]
        },
        {
            title: "2. Grabación de Audio",
            content: [
                "Con su permiso:",
                "• Esta sesión será grabada en audio.",
                "• La grabación se utilizará únicamente para fines de evaluación y documentación.",
                "• La grabación no se utilizará para marketing, investigación o distribución pública.",
                "La grabación será procesada a través de software seguro para convertir el habla en texto (transcripción)."
            ]
        },
        {
            title: "3. Uso de Inteligencia Artificial (IA)",
            content: [
                "Para ayudar con la documentación y evaluación:",
                "• La grabación de audio se convertirá en una transcripción escrita.",
                "• La transcripción será analizada utilizando sistemas seguros de inteligencia artificial (IA).",
                "• El sistema de IA ayuda a generar:",
                "  • Puntuación de evaluación",
                "  • Resúmenes de casos",
                "  • Notas de planificación de servicios",
                "  • Informes estructurados",
                "Importante:",
                "• La IA se utiliza solo como una herramienta de apoyo.",
                "• Un trabajador social humano revisa y puede editar todos los resultados.",
                "• Las decisiones finales son tomadas por el trabajador social, no por la IA."
            ]
        },
        {
            title: "4. Manejo de Datos y Privacidad",
            content: [
                "Su información será:",
                "• Almacenada en servidores seguros.",
                "• Protegida mediante encriptación durante la transmisión.",
                "• Accesible solo para personal autorizado involucrado en su atención.",
                "Su información NO será:",
                "• Vendida",
                "• Compartida con terceros sin su consentimiento (a menos que lo exija la ley)",
                "• Utilizada para el entrenamiento de modelos de IA (donde se apliquen configuraciones de exclusión)"
            ]
        },
        {
            title: "5. Retención de Datos",
            content: [
                "• La grabación de audio y la documentación relacionada pueden conservarse por requisitos organizativos y legales.",
                "• Los registros pueden eliminarse de acuerdo con la política de retención de datos de la organización.",
                "• Puede solicitar información sobre cuánto tiempo se conservan sus datos."
            ]
        },
        {
            title: "6. Sus Derechos",
            content: [
                "Usted tiene derecho a:",
                "• Hacer preguntas sobre cómo se utilizan sus datos.",
                "• Retirar el consentimiento antes o durante la grabación.",
                "• Solicitar acceso a sus registros (sujeto a la política organizacional y límites legales).",
                "• Solicitar corrección de información inexacta.",
                "Si retira su consentimiento:",
                "• La grabación se detendrá inmediatamente.",
                "• Los datos recopilados previamente pueden conservarse si es necesario para fines legales o de documentación."
            ]
        },
        {
            title: "7. Participación Voluntaria",
            content: [
                "Su participación en esta grabación es voluntaria.",
                "Puede negarse a ser grabado.",
                "Rechazar no le impedirá automáticamente recibir servicios, aunque los métodos de documentación pueden diferir."
            ]
        },
        {
            title: "8. Reconocimiento y Consentimiento",
            content: [
                "Al firmar a continuación, confirma que:",
                "• Entiende que esta sesión será grabada en audio.",
                "• Entiende que se utilizarán herramientas de IA para transcribir y ayudar con la evaluación.",
                "• Ha tenido la oportunidad de hacer preguntas.",
                "• Consiente voluntariamente proceder."
            ]
        }
    ],
    zh: [
        {
            title: "1. 录音目的",
            content: [
                "您正在参与上述机构的接纳/评估会议。",
                "本次会议的目的是：",
                "• 了解您的现状",
                "• 进行结构化需求评估",
                "• 生成个案报告以支持服务规划",
                "• 协助个案工作者确定适当的支持"
            ]
        },
        {
            title: "2. 音频录制",
            content: [
                "经您许可：",
                "• 本次会议将进行录音。",
                "• 录音仅用于评估和记录目的。",
                "• 录音不会用于营销、研究或公开发布。",
                "录音将通过安全软件处理，将语音转换为文本（转录）。"
            ]
        },
        {
            title: "3. 人工智能（AI）的使用",
            content: [
                "为协助记录和评估：",
                "• 录音将转换为书面成绩单。",
                "• 成绩单将使用安全的人工智能（AI）系统进行分析。",
                "• AI 系统有助于生成：",
                "  • 评估评分",
                "  • 个案摘要",
                "  • 服务规划说明",
                "  • 结构化报告",
                "重要提示：",
                "• AI 仅用作支持工具。",
                "• 人类个案工作者会审查并可能编辑所有输出。",
                "• 最终决定由个案工作者做出，而不是由 AI 做出。"
            ]
        },
        {
            title: "4. 数据处理和隐私",
            content: [
                "您的信息将：",
                "• 存储在安全服务器上。",
                "• 在传输过程中使用加密保护。",
                "• 仅限参与您护理的授权人员访问。",
                "您的信息将不会：",
                "• 被出售",
                "• 未经您同意与第三方共享（除非法律要求）",
                "• 用于 AI 模型训练（已应用选择退出设置）"
            ]
        },
        {
            title: "5. 数据保留",
            content: [
                "• 录音和相关文件可能会根据组织和法律记录保持要求保留。",
                "• 记录可能会根据组织的数据保留政策删除。",
                "• 您可以请求有关数据保留时间的信息。"
            ]
        },
        {
            title: "6. 您的权利",
            content: [
                "您有权：",
                "• 询问有关如何使用您数据的问题。",
                "• 在录音之前或期间撤回同意。",
                "• 请求访问您的记录（受组织政策和法律限制）。",
                "• 请求更正不准确的信息。",
                "如果您撤回同意：",
                "• 录音将立即停止。",
                "• 如果出于法律或记录目的需要，可能会保留以前收集的数据。"
            ]
        },
        {
            title: "7. 自愿参与",
            content: [
                "您参与本次录音是自愿的。",
                "您可以拒绝被录音。",
                "拒绝不会自动阻止您获得服务，尽管记录方法可能会有所不同。"
            ]
        },
        {
            title: "8. 确认和同意",
            content: [
                "在下方签名即表示您确认：",
                "• 您了解本次会议将进行录音。",
                "• 您了解将使用 AI 工具转录并协助评估。",
                "• 您有机会提问。",
                "• 您自愿同意继续。"
            ]
        }
    ],
    ht: [
        {
            title: "1. Objektif Anrejistreman an",
            content: [
                "Ou ap patisipe nan yon sesyon admisyon / evalyasyon ak òganizasyon ki nonmen pi wo a.",
                "Objektif sesyon sa a se pou:",
                "• Konprann sitiyasyon ou ye kounye a",
                "• Fè yon evalyasyon bezwen estriktire",
                "• Jenere yon rapò ka pou sipòte planifikasyon sèvis",
                "• Ede travayè ka a idantifye sipò apwopriye"
            ]
        },
        {
            title: "2. Anrejistreman Odyo",
            content: [
                "Avèk pèmisyon ou:",
                "• Sesyon sa a pral anrejistre an odyo.",
                "• Anrejistreman an pral itilize sèlman pou rezon evalyasyon ak dokimantasyon.",
                "• Anrejistreman an pap itilize pou maketing, rechèch, oswa distribisyon piblik.",
                "Anrejistreman an pral trete atravè lojisyèl sekirize pou konvèti lapawòl an tèks (transkripsyon)."
            ]
        },
        {
            title: "3. Itilizasyon Entèlijans Atifisyèl (AI)",
            content: [
                "Pou ede avèk dokimantasyon ak evalyasyon:",
                "• Anrejistreman odyo a pral konvèti an yon transkripsyon ekri.",
                "• Transkripsyon an pral analize lè l sèvi avèk sistèm entèlijans atifisyèl (AI) sekirize.",
                "• Sistèm AI a ede jenere:",
                "  • Nòt evalyasyon",
                "  • Rezime ka",
                "  • Nòt planifikasyon sèvis",
                "  • Rapò estriktire",
                "Enpòtan:",
                "• AI itilize kòm yon zouti sipò sèlman.",
                "• Yon travayè ka imen revize e ka modifye tout rezilta yo.",
                "• Desizyon final yo pran pa travayè ka a, pa pa AI."
            ]
        },
        {
            title: "4. Manyen Done ak Konfidansyalite",
            content: [
                "Enfòmasyon ou yo pral:",
                "• Estoke sou sèvè sekirize.",
                "• Pwoteje lè l sèvi avèk chifreman pandan transmisyon.",
                "• Aksesib sèlman pou pèsonèl otorize ki enplike nan swen ou.",
                "Enfòmasyon ou yo PA PRAL:",
                "• Vann",
                "• Pataje ak twazyèm pati san konsantman ou (sof si lalwa egzije sa)",
                "• Itilize pou fòmasyon modèl AI (kote paramèt refi yo aplike)"
            ]
        },
        {
            title: "5. Retansyon Done",
            content: [
                "• Anrejistreman odyo ak dokimantasyon ki gen rapò ka kenbe pou kondisyon òganizasyonèl ak legal.",
                "• Dosye yo ka efase an akò ak politik retansyon done òganizasyon an.",
                "• Ou ka mande enfòmasyon sou konbyen tan done ou yo kenbe."
            ]
        },
        {
            title: "6. Dwa Ou yo",
            content: [
                "Ou gen dwa pou:",
                "• Poze kesyon sou fason done ou yo itilize.",
                "• Retire konsantman anvan oswa pandan anrejistreman an.",
                "• Mande aksè nan dosye ou yo (sijè a politik òganizasyonèl ak limit legal).",
                "• Mande koreksyon enfòmasyon ki pa kòrèk.",
                "Si ou retire konsantman:",
                "• Anrejistreman an ap sispann imedyatman.",
                "• Done yo te kolekte deja ka kenbe si sa nesesè pou rezon legal oswa dokimantasyon."
            ]
        },
        {
            title: "7. Patisipasyon Volontè",
            content: [
                "Patisipasyon ou nan anrejistreman sa a se volontè.",
                "Ou ka refize anrejistre.",
                "Refize pa pral otomatikman anpeche ou resevwa sèvis, byenke metòd dokimantasyon ka diferan."
            ]
        },
        {
            title: "8. Rekonesans ak Konsantman",
            content: [
                "Lè ou siyen anba a, ou konfime ke:",
                "• Ou konprann ke sesyon sa a pral anrejistre an odyo.",
                "• Ou konprann ke zouti AI pral itilize pou transkri ak ede avèk evalyasyon.",
                "• Ou te gen opòtinite pou poze kesyon.",
                "• Ou volontèman konsanti pou kontinye."
            ]
        }
    ],
    ar: [
        {
            title: "1. الغرض من التسجيل",
            content: [
                "أنت تشارك في جلسة قبول / تقييم مع المنظمة المذكورة أعلاه.",
                "الغرض من هذه الجلسة هو:",
                "• فهم وضعك الحالي",
                "• إجراء تقييم منظم للاحتياجات",
                "• إنشاء تقرير حالة لدعم تخطيط الخدمة",
                "• مساعدة الاخصائي الاجتماعي في تحديد الدعامات المناسبة"
            ]
        },
        {
            title: "2. التسجيل الصوتي",
            content: [
                "بإذنك:",
                "• سيتم تسجيل هذه الجلسة صوتيًا.",
                "• سيتم استخدام التسجيل فقط لأغراض التقييم والتوثيق.",
                "• لن يتم استخدام التسجيل للتسويق أو البحث أو التوزيع العام.",
                "سيتم معالجة التسجيل من خلال برنامج آمن لتحويل الكلام إلى نص (النسخ)."
            ]
        },
        {
            title: "3. استخدام الذكاء الاصطناعي (AI)",
            content: [
                "للمساعدة في التوثيق والتقييم:",
                "• سيتم تحويل التسجيل الصوتي إلى نص مكتوب.",
                "• سيتم تحليل النص باستخدام أنظمة ذكاء اصطناعي (AI) آمنة.",
                "• يساعد نظام الذكاء الاصطناعي في إنشاء:",
                "  • درجات التقييم",
                "  • ملخصات الحالة",
                "  • ملاحظات تخطيط الخدمة",
                "  • تقارير منظمة",
                "مهم:",
                "• يستخدم الذكاء الاصطناعي كأداة دعم فقط.",
                "• يقوم اخصائي اجتماعي بشري بمراجعة وقد يقوم بتحرير جميع المخرجات.",
                "• يتم اتخاذ القرارات النهائية من قبل الاخصائي الاجتماعي، وليس بواسطة الذكاء الاصطناعي."
            ]
        },
        {
            title: "4. التعامل مع البيانات والخصوصية",
            content: [
                "ستكون معلوماتك:",
                "• مخزنة على خوادم آمنة.",
                "• محمية باستخدام التشفير أثناء النقل.",
                "• متاحة فقط للموظفين المصرح لهم المشاركين في رعايتك.",
                "معلوماتك لن:",
                "• تباع",
                "• تتم مشاركتها مع أطراف ثالثة دون موافقتك (ما لم يكن ذلك مطلوبًا بموجب القانون)",
                "• تستخدم لتدريب نماذج الذكاء الاصطناعي (حيث يتم تطبيق إعدادات إلغاء الاشتراك)"
            ]
        },
        {
            title: "5. الاحتفاظ بالبيانات",
            content: [
                "• قد يتم الاحتفاظ بالتسجيل الصوتي والوثائق ذات الصلة للمتطلبات التنظيمية والقانونية لحفظ السجلات.",
                "• قد يتم حذف السجلات وفقًا لسياسة الاحتفاظ بالبيانات الخاصة بالمنظمة.",
                "• يمكنك طلب معلومات حول مدة الاحتفاظ ببياناتك."
            ]
        },
        {
            title: "6. حقوقك",
            content: [
                "لديك الحق في:",
                "• طرح أسئلة حول كيفية استخدام بياناتك.",
                "• سحب الموافقة قبل أو أثناء التسجيل.",
                "• طلب الوصول إلى سجلاتك (تخضع للسياسة التنظيمية والحدود القانونية).",
                "• طلب تصحيح المعلومات غير الدقيقة.",
                "إذا سحبت موافقتك:",
                "• سيتوقف التسجيل فورًا.",
                "• قد يتم الاحتفاظ بالبيانات التي تم جمعها مسبقًا إذا كان ذلك مطلوبًا لأغراض قانونية أو توثيقية."
            ]
        },
        {
            title: "7. المشاركة الطوعية",
            content: [
                "مشاركتك في هذا التسجيل طوعية.",
                "يمكنك رفض التسجيل.",
                "الرفض لن يمنعك تلقائيًا من تلقي الخدمات، على الرغم من أن طرق التوثيق قد تختلف."
            ]
        },
        {
            title: "8. الإقرار والموافقة",
            content: [
                "بالتوقيع أدناه، تؤكد أنك:",
                "• تفهم أن هذه الجلسة سيتم تسجيلها صوتيًا.",
                "• تفهم أنه سيتم استخدام أدوات الذكاء الاصطناعي لنسخ والمساعدة في التقييم.",
                "• أتيحت لك الفرصة لطرح الأسئلة.",
                "• توافق طوعًا على المضي قدمًا."
            ]
        }
    ],
    so: [
        {
            title: "1. Ujeeddada Duubista",
            content: [
                "Waxaad ka qaybqaadanaysaa fadhi qaadasho / qiimayn ah oo lala yeelanayo ururka kor ku magacaaban.",
                "Ujeeddada fadhigan waa:",
                "• In la fahmo xaaladdaada hadda",
                "• In la sameeyo qiimayn baahida loo qabo oo habaysan",
                "• In la soo saaro warbixin kiis si loo taageero qorshaynta adeegga",
                "• In la caawiyo shaqaalaha kiiska si loo aqoonsado taageerooyinka habboon"
            ]
        },
        {
            title: "2. Duubista Maqalka",
            content: [
                "Iyadoo ogolaanshahaaga la haysto:",
                "• Fadhigan waxaa lagu duubi doonaa maqal ah.",
                "• Duubista waxaa loo isticmaali doonaa oo kaliya ujeedooyin qiimeyn iyo dukumenti ah.",
                "• Duubista looma isticmaali doono suuqgeyn, cilmi baaris, ama baahin dadweyne.",
                "Duubista waxaa lagu farsameyn doonaa software ammaan ah si hadalka loogu beddelo qoraal (transcription)."
            ]
        },
        {
            title: "3. Isticmaalka Sirdoonka Macmalka ah (AI)",
            content: [
                "Si looga caawiyo dukumentiyada iyo qiimeynta:",
                "• Duubista maqalka waxaa loo beddeli doonaa qoraal qoran.",
                "• Qoraalka waxaa lagu falanqeyn doonaa iyadoo la isticmaalayo nidaamyada sirdoonka macmalka ah (AI) ee sugan.",
                "• Nidaamka AI wuxuu caawiyaa dhalinta:",
                "  • Qiimaynta dhalinta",
                "  • Koobitaanka kiisaska",
                "  • Qoraallada qorshaynta adeegga",
                "  • Warbixinnada habaysan",
                "Muhiim:",
                "• AI waxaa loo isticmaalaa sidii qalab taageero oo kaliya.",
                "• Shaqaale kiis oo bini'aadam ah ayaa dib u eegis ku sameeya oo wax ka beddeli kara dhammaan wax soo saarka.",
                "• Go'aanada ugu dambeeya waxaa gaara shaqaalaha kiiska, ee ma ahan AI."
            ]
        },
        {
            title: "4. Maareynta Xogta iyo Qarsoodiga",
            content: [
                "Macluumaadkaaga waxay noqon doonaan:",
                "• Lagu kaydiyaa server-yo sugan.",
                "• Lagu ilaaliyaa iyadoo la isticmaalayo sir (encryption) inta lagu jiro gudbinta.",
                "• Waxaa mari kara oo kaliya shaqaalaha idman ee ku lug leh daryeelkaaga.",
                "Macluumaadkaaga MA NOQON DOONO:",
                "• In la iibiyo",
                "• In lala wadaago qolo saddexaad iyada oo aan oggolaanshahaaga la helin (haddii uusan sharcigu u baahnayn)",
                "• In loo isticmaalo tababarka moodalka AI (halka goobaha ka-bixitaanka laga codsado)"
            ]
        },
        {
            title: "5. Haysashada Xogta",
            content: [
                "• Duubista maqalka iyo dukumentiyada la xiriira waxaa loo hayn karaa shuruudaha haynta diiwaanka ururka iyo sharciga.",
                "• Diiwaanka waxaa la tirtiri karaa si waafaqsan siyaasadda haysashada xogta ee ururka.",
                "• Waxaad codsan kartaa macluumaad ku saabsan inta ay xogtaada haynayaan."
            ]
        },
        {
            title: "6. Xuquuqdaada",
            content: [
                "Waxaad xaq u leedahay inaad:",
                "• Weydiiso su'aalo ku saabsan sida xogtaada loo isticmaalo.",
                "• Ka noqoto ogolaanshaha ka hor ama inta lagu jiro duubista.",
                "• Codso marin u helka diiwaankaaga (iyadoo ku xiran siyaasadda ururka iyo xadka sharciga ah).",
                "• Codso sixitaanka macluumaadka khaldan.",
                "Haddii aad ka noqoto ogolaanshaha:",
                "• Duubista waxay joogsan doontaa isla markiiba.",
                "• Xogta horay loo soo ururiyay waa la hayn karaa haddii loo baahdo ujeedooyin sharci ama dukumenti ah."
            ]
        },
        {
            title: "7. Ka Qaybgalka Ikhtiyaarka ah",
            content: [
                "Ka qaybgalkaaga duubistan waa ikhtiyaari.",
                "Waad diidi kartaa in lagu duubo.",
                "Diidmadu si toos ah kaama hor istaagi doonto helitaanka adeegyada, inkasta oo hababka dukumentigu ay kala duwanaan karaan."
            ]
        },
        {
            title: "8. Qirasho iyo Ogolaansho",
            content: [
                "Markaad saxiixdo xagga hoose, waxaad xaqiijinaysaa in:",
                "• Waxaad fahamsan tahay in fadhigan lagu duubi doono maqal ah.",
                "• Waxaad fahamsan tahay in qalabka AI loo isticmaali doono in lagu qoro laguna caawiyo qiimeynta.",
                "• Waxaad fursad u heshay inaad su'aalo weydiiso.",
                "• Waxaad si ikhtiyaari ah u ogolaatay inaad sii socoto."
            ]
        }
    ],
    tr: [
        {
            title: "1. Kaydın Amacı",
            content: [
                "Yukarıda adı geçen kurumla bir kabul / değerlendirme görüşmesine katılıyorsunuz.",
                "Bu görüşmenin amacı:",
                "• Mevcut durumunuzu anlamak",
                "• Yapılandırılmış bir ihtiyaç değerlendirmesi yapmak",
                "• Hizmet planlamasını desteklemek için bir vaka raporu oluşturmak",
                "• Vaka sorumlusunun uygun destekleri belirlemesine yardımcı olmak"
            ]
        },
        {
            title: "2. Ses Kaydı",
            content: [
                "İzninizle:",
                "• Bu görüşme sesli olarak kaydedilecektir.",
                "• Kayıt sadece değerlendirme ve belgeleme amaçları için kullanılacaktır.",
                "• Kayıt pazarlama, araştırma veya kamuya açık dağıtım için kullanılmayacaktır.",
                "Kayıt, konuşmayı metne dönüştürmek (transkripsiyon) için güvenli yazılım aracılığıyla işlenecektir."
            ]
        },
        {
            title: "3. Yapay Zeka (YZ) Kullanımı",
            content: [
                "Belgeleme ve değerlendirmeye yardımcı olmak için:",
                "• Ses kaydı yazılı bir transkripte dönüştürülecektir.",
                "• Transkript, güvenli yapay zeka (YZ) sistemleri kullanılarak analiz edilecektir.",
                "• YZ sistemi şunları oluşturmaya yardımcı olur:",
                "  • Değerlendirme puanlaması",
                "  • Vaka özetleri",
                "  • Hizmet planlama notları",
                "  • Yapılandırılmış raporlar",
                "Önemli:",
                "• YZ sadece destek aracı olarak kullanılır.",
                "• İnsan bir vaka sorumlusu tüm çıktıları inceler ve düzenleyebilir.",
                "• Nihai kararlar YZ tarafından değil, vaka sorumlusu tarafından verilir."
            ]
        },
        {
            title: "4. Veri İşleme ve Gizlilik",
            content: [
                "Bilgileriniz:",
                "• Güvenli sunucularda saklanacaktır.",
                "• İletim sırasında şifreleme kullanılarak korunacaktır.",
                "• Sadece bakımınızla ilgilenen yetkili personel tarafından erişilebilir olacaktır.",
                "Bilgileriniz ŞUNLARI YAPMAYACAKTIR:",
                "• Satılmayacaktır",
                "• İzniniz olmadan üçüncü şahıslarla paylaşılmayacaktır (yasalar gerektirmedikçe)",
                "• YZ model eğitimi için kullanılmayacaktır (devre dışı bırakma ayarlarının uygulandığı yerlerde)"
            ]
        },
        {
            title: "5. Veri Saklama",
            content: [
                "• Ses kaydı ve ilgili belgeler, kurumsal ve yasal kayıt tutma gereklilikleri için saklanabilir.",
                "• Kayıtlar, kurumun veri saklama politikasına uygun olarak silinebilir.",
                "• Verilerinizin ne kadar süreyle saklandığı hakkında bilgi talep edebilirsiniz."
            ]
        },
        {
            title: "6. Haklarınız",
            content: [
                "Şu haklara sahipsiniz:",
                "• Verilerinizin nasıl kullanıldığı hakkında soru sormak.",
                "• Kayıt öncesinde veya sırasında onamınızı geri çekmek.",
                "• Kayıtlarınıza erişim talep etmek (kurumsal politikaya ve yasal sınırlara tabidir).",
                "• Hatalı bilgilerin düzeltilmesini talep etmek.",
                "Onamınızı geri çekerseniz:",
                "• Kayıt derhal duracaktır.",
                "• Daha önce toplanan veriler, yasal veya belgeleme amaçları için gerekliyse saklanabilir."
            ]
        },
        {
            title: "7. Gönüllü Katılım",
            content: [
                "Bu kayda katılımınız gönüllüdür.",
                "Kaydedilmeyi reddedebilirsiniz.",
                "Reddetmek, belgeleme yöntemleri farklılık gösterse de hizmet almanızı otomatik olarak engellemez."
            ]
        },
        {
            title: "8. Kabul ve Onam",
            content: [
                "Aşağıyı imzalayarak şunları onaylıyorsunuz:",
                "• Bu görüşmenin sesli olarak kaydedileceğini anlıyorsunuz.",
                "• Transkripsiyon ve değerlendirmeye yardımcı olmak için YZ araçlarının kullanılacağını anlıyorsunuz.",
                "• Soru sorma fırsatınız oldu.",
                "• İşleme devam etmeyi gönüllü olarak kabul ediyorsunuz."
            ]
        }
    ]
};

const UI_TRANSLATIONS = {
    en: {
        title: "CLIENT INFORMED CONSENT",
        subtitle: "Audio Recording and AI-Assisted Assessment",
        org_label: "Organization Name",
        date_label: "Date",
        client_label: "Client Name",
        id_label: "Client ID",
        worker_label: "Case Worker Name",
        client_sig_label: "Client Signature",
        worker_sig_label: "Case Worker Signature",
        client_sig_desc: "By signing, I verify that I am the client named above and I consent to the terms outlined.",
        worker_sig_desc: "By signing, I verify that I have explained the consent form to the client.",
        placeholder_client: "Enter client name",
        placeholder_worker: "Enter case worker name",
        cancel: "Cancel",
        submit: "Sign & Download",
        generating: "Generating PDF...",
        warning: "Signatures are required to proceed."
    },
    fr: {
        title: "CONSENTEMENT ÉCLAIRÉ DU CLIENT",
        subtitle: "Enregistrement audio et évaluation assistée par IA",
        org_label: "Nom de l'organisation",
        date_label: "Date",
        client_label: "Nom du client",
        id_label: "ID Client",
        worker_label: "Nom du travailleur social",
        client_sig_label: "Signature du client",
        worker_sig_label: "Signature du travailleur social",
        client_sig_desc: "En signant, je confirme que je suis le client nommé ci-dessus et je consens aux conditions décrites.",
        worker_sig_desc: "En signant, je confirme avoir expliqué le formulaire de consentement au client.",
        placeholder_client: "Entrez le nom du client",
        placeholder_worker: "Entrez le nom du travailleur social",
        cancel: "Annuler",
        submit: "Signer et télécharger",
        generating: "Génération du PDF...",
        warning: "Les signatures sont requises pour continuer."
    },
    es: {
        title: "CONSENTIMIENTO INFORMADO DEL CLIENTE",
        subtitle: "Grabación de audio y evaluación asistida por IA",
        org_label: "Nombre de la organización",
        date_label: "Fecha",
        client_label: "Nombre del cliente",
        id_label: "ID del cliente",
        worker_label: "Nombre del trabajador social",
        client_sig_label: "Firma del cliente",
        worker_sig_label: "Firma del trabajador social",
        client_sig_desc: "Al firmar, verifico que soy el cliente mencionado anteriormente y doy mi consentimiento a los términos descritos.",
        worker_sig_desc: "Al firmar, verifico que he explicado el formulario de consentimiento al cliente.",
        placeholder_client: "Ingrese el nombre del cliente",
        placeholder_worker: "Ingrese el nombre del trabajador social",
        cancel: "Cancelar",
        submit: "Firmar y descargar",
        generating: "Generando PDF...",
        warning: "Se requieren firmas para continuar."
    },
    zh: {
        title: "客户知情同意书",
        subtitle: "录音和 AI 辅助评估",
        org_label: "机构名称",
        date_label: "日期",
        client_label: "客户姓名",
        id_label: "客户 ID",
        worker_label: "个案工作者姓名",
        client_sig_label: "客户签名",
        worker_sig_label: "个案工作者签名",
        client_sig_desc: "签名即表示我确认我是上述客户，并同意所述条款。",
        worker_sig_desc: "签名即表示我确认已向客户解释同意书。",
        placeholder_client: "输入客户姓名",
        placeholder_worker: "输入个案工作者姓名",
        cancel: "取消",
        submit: "签署并下载",
        generating: "正在生成 PDF...",
        warning: "需要签名才能继续。"
    },
    ht: {
        title: "KONSANTMAN ENFÒME KLIYAN",
        subtitle: "Anrejistreman Odyo ak Evalyasyon Asiste pa AI",
        org_label: "Non Òganizasyon",
        date_label: "Dat",
        client_label: "Non Kliyan",
        id_label: "ID Kliyan",
        worker_label: "Non Travayè Sosyal",
        client_sig_label: "Siyati Kliyan",
        worker_sig_label: "Siyati Travayè Sosyal",
        client_sig_desc: "Lè mwen siyen, mwen verifye ke mwen se kliyan ki nonmen pi wo a epi mwen dakò ak kondisyon yo.",
        worker_sig_desc: "Lè mwen siyen, mwen verifye ke mwen te eksplike fòmilè konsantman an bay kliyan an.",
        placeholder_client: "Antre non kliyan an",
        placeholder_worker: "Antre non travayè sosyal la",
        cancel: "Anile",
        submit: "Siyen epi telechaje",
        generating: "Y ap jenere PDF...",
        warning: "Siyati yo obligatwa pou kontinye."
    },
    ar: {
        title: "الموافقة المستنيرة للعميل",
        subtitle: "التسجيل الصوتي والتقييم بمساعدة الذكاء الاصطناعي",
        org_label: "اسم المنظمة",
        date_label: "التاريخ",
        client_label: "اسم العميل",
        id_label: "معرف العميل",
        worker_label: "اسم الاخصائي الاجتماعي",
        client_sig_label: "توقيع العميل",
        worker_sig_label: "توقيع الاخصائي الاجتماعي",
        client_sig_desc: "بالتوقيع، أؤكد أنني العميل المذكور أعلاه وأوافق على الشروط الموضحة.",
        worker_sig_desc: "بالتوقيع، أؤكد أنني شرحت نموذج الموافقة للعميل.",
        placeholder_client: "أدخل اسم العميل",
        placeholder_worker: "أدخل اسم الاخصائي الاجتماعي",
        cancel: "إلغاء",
        submit: "التوقع والتحميل",
        generating: "جاري إنشاء ملف PDF...",
        warning: "التوقيعات مطلوبة للمتابعة."
    },
    so: {
        title: "OGOLAANSHAHA MACMIILKA",
        subtitle: "Duubista Maqalka iyo Qiimaynta Caawinta AI",
        org_label: "Magaca Ururka",
        date_label: "Taariikhda",
        client_label: "Magaca Macmiilka",
        id_label: "ID-ga Macmiilka",
        worker_label: "Magaca Shaqaalaha Kiiska",
        client_sig_label: "Saxiixa Macmiilka",
        worker_sig_label: "Saxiixa Shaqaalaha Kiiska",
        client_sig_desc: "Markaan saxiixo, waxaan xaqiijinayaa inaan ahay macmiilka kor lagu magacaabay oo aan oggolahay shuruudaha lagu qeexay.",
        worker_sig_desc: "Markaan saxiixo, waxaan xaqiijinayaa inaan macmiilka u sharaxay foomka ogolaanshaha.",
        placeholder_client: "Gali magaca macmiilka",
        placeholder_worker: "Gali magaca shaqaalaha kiiska",
        cancel: "Ka noqo",
        submit: "Saxiix & Soo deji",
        generating: "Dhisidda PDF...",
        warning: "Saxiixyada ayaa loo baahan yahay si loo sii socdo."
    },
    tr: {
        title: "MÜŞTERİ BİLGİLENDİRİLMİŞ ONAM FORMU",
        subtitle: "Ses Kaydı ve YZ Destekli Değerlendirme",
        org_label: "Kurum Adı",
        date_label: "Tarih",
        client_label: "Müşteri Adı",
        id_label: "Müşteri Kimliği",
        worker_label: "Vaka Sorumlusu Adı",
        client_sig_label: "Müşteri İmzası",
        worker_sig_label: "Vaka Sorumlusu İmzası",
        client_sig_desc: "İmzalayarak, yukarıda adı geçen müşteri olduğumu ve belirtilen şartları onayladığımı beyan ederim.",
        worker_sig_desc: "İmzalayarak, onam formunu müşteriye açıkladığımı beyan ederim.",
        placeholder_client: "Müşteri adını girin",
        placeholder_worker: "Vaka sorumlusu adını girin",
        cancel: "İptal",
        submit: "İmzala ve İndir",
        generating: "PDF oluşturuluyor...",
        warning: "Devam etmek için imzalar gereklidir."
    }
};

export function ConsentDialog({ open, onOpenChange, caseData, onComplete }) {
    const [step, setStep] = useState(1); // 1: Info, 2: Sign
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [language, setLanguage] = useState("en");

    // Form State
    const [workerName, setWorkerName] = useState("");
    const [clientName, setClientName] = useState(caseData?.client_name || "");
    const [clientSignature, setClientSignature] = useState(null);
    const [workerSignature, setWorkerSignature] = useState(null);
    const [date] = useState(new Date().toLocaleDateString());

    const content = CONSENT_TRANSLATIONS[language] || CONSENT_TRANSLATIONS["en"];
    const uiLabels = UI_TRANSLATIONS[language] || UI_TRANSLATIONS["en"];

    const handleSubmit = async () => {
        if (!workerName || !clientName || !clientSignature || !workerSignature) {
            toast.error("Please complete all fields and signatures");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await api.generateConsentForm(caseData.id, {
                client_name: clientName,
                worker_name: workerName,
                client_signature: clientSignature,
                worker_signature: workerSignature,
                date: date,
                language: language,
                sections: content,
                ui_labels: uiLabels
            });

            // Download PDF
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Consent_${caseData.client_id}_A${caseData.assessment_number || 1}_${clientName.replace(/\s+/g, '_')}_${language}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            toast.success("Consent recorded and PDF downloaded");
            onComplete(); // Notify parent to close and update state
        } catch (error) {
            console.error("Failed to submit consent:", error);
            toast.error("Failed to generate consent PDF");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0 gap-0">
                <DialogHeader className="p-6 border-b border-slate-100 flex-shrink-0 flex-row items-center justify-between space-y-0">
                    <div>
                        <DialogTitle className="text-xl font-bold font-['Manrope']">
                            {uiLabels.title}
                        </DialogTitle>
                        <DialogDescription>
                            {uiLabels.subtitle}
                        </DialogDescription>
                    </div>
                    <div className="w-[140px]">
                        <Select value={language} onValueChange={setLanguage}>
                            <SelectTrigger className="h-9">
                                <Languages className="w-4 h-4 mr-2 text-slate-500" />
                                <SelectValue placeholder="Language" />
                            </SelectTrigger>
                            <SelectContent>
                                {LANGUAGES.map(lang => (
                                    <SelectItem key={lang.code} value={lang.code}>
                                        {lang.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </DialogHeader>

                <ScrollArea className="flex-1 p-6" dir={language === 'ar' ? 'rtl' : 'ltr'}>
                    <div className="max-w-3xl mx-auto space-y-8">

                        {/* Header Info */}
                        <div className="grid grid-cols-2 gap-x-8 gap-y-4 bg-slate-50 p-6 rounded-md border border-slate-200">
                            <div>
                                <Label className="text-slate-500 text-xs uppercase tracking-wider">{uiLabels.org_label}</Label>
                                <div className="font-medium text-slate-900 mt-1">Solutions For Good</div>
                            </div>
                            <div>
                                <Label className="text-slate-500 text-xs uppercase tracking-wider">{uiLabels.date_label}</Label>
                                <div className="font-medium text-slate-900 mt-1">{date}</div>
                            </div>
                            <div>
                                <Label htmlFor="clientName" className="text-slate-500 text-xs uppercase tracking-wider">{uiLabels.client_label}</Label>
                                <Input
                                    id="clientName"
                                    value={clientName}
                                    onChange={(e) => setClientName(e.target.value)}
                                    className="mt-1 h-9 bg-white"
                                    placeholder={uiLabels.placeholder_client}
                                />
                            </div>
                            <div>
                                <Label className="text-slate-500 text-xs uppercase tracking-wider">{uiLabels.id_label}</Label>
                                <div className="font-medium text-slate-900 mt-1">{caseData?.client_id || "Unknown"}</div>
                            </div>
                            <div className="col-span-2">
                                <Label htmlFor="workerName" className="text-slate-500 text-xs uppercase tracking-wider">{uiLabels.worker_label}</Label>
                                <Input
                                    id="workerName"
                                    value={workerName}
                                    onChange={(e) => setWorkerName(e.target.value)}
                                    className="mt-1 h-9 bg-white"
                                    placeholder={uiLabels.placeholder_worker}
                                />
                            </div>
                        </div>

                        {/* Consent Text */}
                        <div className="space-y-8">
                            {content.map((section, idx) => (
                                <div key={idx} className="space-y-3">
                                    <h3 className="font-bold text-slate-900 border-b border-slate-200 pb-2">
                                        {section.title}
                                    </h3>
                                    <div className="text-sm text-slate-700 space-y-2 leading-relaxed pl-2">
                                        {section.content.map((line, k) => (
                                            <p key={k} className={line.startsWith("Important:") || line.startsWith("Important") || line.startsWith("Enpòtan") || line.startsWith("مهم") ? "font-bold text-amber-700 mt-4" : ""}>
                                                {line}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Signatures */}
                        <div className="grid md:grid-cols-2 gap-8 pt-8 border-t-2 border-slate-100" dir="ltr">
                            <div>
                                <SignaturePad
                                    id="client-sig"
                                    label={uiLabels.client_sig_label}
                                    onEnd={setClientSignature}
                                />
                                <div className="mt-2 text-xs text-slate-500">
                                    {uiLabels.client_sig_desc}
                                </div>
                            </div>

                            <div>
                                <SignaturePad
                                    id="worker-sig"
                                    label={uiLabels.worker_sig_label}
                                    onEnd={setWorkerSignature}
                                />
                                <div className="mt-2 text-xs text-slate-500">
                                    {uiLabels.worker_sig_desc}
                                </div>
                            </div>
                        </div>

                    </div>
                </ScrollArea>

                <DialogFooter className="p-4 border-t border-slate-100 bg-slate-50 flex-shrink-0 flex items-center justify-between sm:justify-between">
                    <div className="text-xs text-slate-500 italic flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        {uiLabels.warning}
                    </div>
                    <div className="flex gap-3">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            {uiLabels.cancel}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            disabled={isSubmitting || !clientSignature || !workerSignature || !workerName || !clientName}
                            className="bg-blue-600 hover:bg-blue-700 min-w-[150px]"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {uiLabels.generating}
                                </>
                            ) : (
                                <>
                                    <FileCheck className="w-4 h-4 mr-2" />
                                    {uiLabels.submit}
                                </>
                            )}
                        </Button>
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
